<template>
  <div :class="['condition-field', { 'condition-field--compact': compact }]">
    <span
      v-if="title"
      class="condition-field__title">
      {{ title }}
    </span>

    <v-select
      v-model="field.type"
      :items="computedFieldTypes"
      item-text="label"
      item-value="value"
      label="Tipo"
      outlined
      dense
      hide-details
      class="condition-field__type"
      @change="selectFieldType(field, false)"
    />

    <v-autocomplete
      v-if="field.type === 'FUNCAO'"
      v-model="field.value"
      :items="fieldFunctions"
      item-text="name"
      item-value="id"
      label="Função"
      outlined
      dense
      hide-details
      class="condition-field__value"
      @change="selectFieldFunction(field)"
    />

    <v-autocomplete
      v-if="field.type === 'COLUNA'"
      v-model="field.value"
      :items="datasourceColumns"
      item-text="label"
      item-value="value"
      label="Coluna"
      outlined
      dense
      hide-details
      class="condition-field__value"
    />

    <template v-if="field.type === 'ALFANUMERICO'">
      <v-combobox
        v-if="hasMultipleValues"
        v-model="field.valuein"
        multiple
        item-text="value"
        label="Valores"
        outlined
        dense
        hide-details
        append-icon="none"
        :delimiters="[',']"
        class="condition-field__value"
        @change="normalizeValueIn(field)"
      />

      <v-text-field
        v-else
        v-model="field.value"
        label="Valor"
        outlined
        dense
        hide-details
        class="condition-field__value"
      />
    </template>

    <template v-if="field.type === 'NUMERO'">
      <v-combobox
        v-if="hasMultipleValues"
        v-model="field.valuein"
        multiple
        item-text="value"
        label="Valores"
        type="number"
        outlined
        dense
        hide-details
        append-icon="none"
        :delimiters="[',']"
        class="condition-field__value"
        @change="normalizeValueIn(field)"
      />

      <v-text-field
        v-else
        v-model="field.value"
        label="Valor"
        type="number"
        outlined
        dense
        hide-details
        class="condition-field__value"
      />
    </template>

    <template v-if="field.type === 'DATA'">
      <v-combobox
        v-if="hasMultipleValues"
        v-model="field.valuein"
        multiple
        item-text="value"
        label="Datas"
        outlined
        dense
        hide-details
        append-icon="none"
        :delimiters="[',']"
        class="condition-field__value"
        @change="normalizeValueIn(field)"
      />

      <v-menu
        v-else
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formatDate(field.value)"
            label="Data"
            prepend-inner-icon="mdi-calendar"
            readonly
            outlined
            dense
            hide-details
            class="condition-field__value"
            v-bind="attrs"
            v-on="on"
          />
        </template>

        <v-date-picker
          v-model="field.value"
          :locale="$i18n.locale"
          @input="dateMenu = false"
        />
      </v-menu>
    </template>

    <v-text-field
      v-if="field.type === 'VALOR_PADRAO'"
      :value="defaultValue"
      label="Valor padrão"
      readonly
      outlined
      dense
      hide-details
      class="condition-field__value"
      @input="setDefaultValue(field)"
    />

    <v-autocomplete
      v-if="field.type === 'CONSTANTES'"
      v-model="field.value"
      :items="constants"
      item-text="label"
      item-value="value"
      label="Constante"
      outlined
      dense
      hide-details
      class="condition-field__value"
    />

    <div
      v-if="field.type === 'FUNCAO' && field.parameters && field.parameters.length"
      class="condition-field__params">
      <div class="condition-field__params-header">
        <span class="condition-field__params-title">
          {{ functionParamsSummary }}
        </span>

        <v-btn
          icon
          x-small
          color="primary"
          :title="functionParamsToggleTitle"
          @click="toggleFunctionParams">
          <v-icon x-small>
            {{ functionParamsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
          </v-icon>
        </v-btn>
      </div>

      <div v-show="functionParamsExpanded">
        <div
          v-for="parameter in field.parameters"
          :key="parameter.name"
          class="condition-field__param">
          <v-tooltip bottom max-width="280">
            <template v-slot:activator="{ on, attrs }">
              <span
                class="condition-field__param-name"
                v-bind="attrs"
                v-on="on">
                <span class="condition-field__param-name-text">
                  {{ parameter.name }}
                </span>
              </span>
            </template>

            <span>{{ parameter.name }}</span>
          </v-tooltip>

          <v-select
            v-model="parameter.type"
            :items="functionParamsTypes"
            item-text="label"
            item-value="value"
            label="Tipo"
            outlined
            dense
            hide-details
            class="condition-field__param-type"
            @change="selectFieldType(parameter, true)"
          />

          <v-text-field
            v-if="parameter.type === 'ALFANUMERICO'"
            v-model="parameter.value"
            label="Valor"
            outlined
            dense
            hide-details
            class="condition-field__param-value"
          />

          <v-text-field
            v-if="parameter.type === 'NUMERO'"
            v-model="parameter.value"
            label="Valor"
            type="number"
            outlined
            dense
            hide-details
            class="condition-field__param-value"
          />

          <v-text-field
            v-if="parameter.type === 'VALOR_PADRAO'"
            :value="defaultValue"
            label="Valor padrão"
            readonly
            outlined
            dense
            hide-details
            class="condition-field__param-value"
            @input="setDefaultValue(parameter)"
          />

          <v-autocomplete
            v-if="parameter.type === 'COLUNA'"
            v-model="parameter.value"
            :items="datasourceColumns"
            item-text="label"
            item-value="value"
            label="Coluna"
            outlined
            dense
            hide-details
            class="condition-field__param-value"
          />

          <v-autocomplete
            v-if="parameter.type === 'CONSTANTES'"
            v-model="parameter.value"
            :items="constants"
            item-text="label"
            item-value="value"
            label="Constante"
            outlined
            dense
            hide-details
            class="condition-field__param-value"
          />

          <v-autocomplete
            v-if="parameter.type === 'FUNCAO'"
            v-model="parameter.value"
            :items="parameterFunctions"
            item-text="name"
            item-value="id"
            label="Função"
            outlined
            dense
            hide-details
            class="condition-field__param-value"
          />

          <v-menu
            v-if="parameter.type === 'DATA'"
            v-model="parameterMenus[parameter.name]"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatDate(parameter.value)"
                label="Data"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                class="condition-field__param-value"
                v-bind="attrs"
                v-on="on"
              />
            </template>

            <v-date-picker
              v-model="parameter.value"
              :locale="$i18n.locale"
              @input="$set(parameterMenus, parameter.name, false)"
            />
          </v-menu>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConditionField',

  props: {
    title: {
      type: String,
      default: '',
    },

    field: {
      type: Object,
      required: true,
    },

    condition: {
      type: Object,
      required: true,
    },

    fieldTypes: {
      type: Array,
      default: () => [],
    },

    fieldFunctions: {
      type: Array,
      default: () => [],
    },

    datasourceColumns: {
      type: Array,
      default: () => [],
    },

    constants: {
      type: Array,
      default: () => [],
    },

    defaultValue: {
      type: [String, Number],
      default: '',
    },

    compact: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    dateMenu: false,
    functionParamsExpanded: true,
    parameterMenus: {},
  }),

  watch: {
    defaultValue() {
      this.syncDefaultValueFields();
    },

    'condition.operator': {
      immediate: true,
      handler() {
        this.hydrateValueIn();
      },
    },
  },

  computed: {
    hasMultipleValues() {
      return this.condition.operator === 'in' || this.condition.operator === 'not in';
    },

    computedFieldTypes() {
      if (this.field.order === 1) {
        return this.fieldTypes.filter((type) => type.showOnRecordField);
      }

      if (this.hasMultipleValues) {
        return this.fieldTypes.filter((type) => type.showOnDefaultValue);
      }

      return this.fieldTypes;
    },

    functionParamsTypes() {
      return this.fieldTypes.filter((type) => type.showOnFunctionParam);
    },

    parameterFunctions() {
      return this.fieldFunctions.filter((fieldFunction) => fieldFunction.id === 'data_atual');
    },

    functionParamsSummary() {
      const paramsCount = this.field.parameters ? this.field.parameters.length : 0;

      return paramsCount === 1
        ? '1 parâmetro'
        : `${paramsCount} parâmetros`;
    },

    functionParamsToggleTitle() {
      return this.functionParamsExpanded
        ? 'Recolher parâmetros da função'
        : 'Expandir parâmetros da função';
    },
  },

  mounted() {
    this.hydrateValueIn();
    this.syncDefaultValueFields();
    this.sortParameters();
  },

  methods: {
    selectFieldType(field, isParameter) {
      if (field.type === 'VALOR_PADRAO') {
        this.$set(field, 'value', this.defaultValue);

        return;
      }

      this.$set(field, 'value', '');
      this.$set(field, 'valuein', []);

      if (!isParameter) {
        this.$set(field, 'parameters', []);
      }
    },

    selectFieldFunction(field) {
      const selectedFunction = this.fieldFunctions
        .find((fieldFunction) => fieldFunction.id === field.value);

      const parameters = selectedFunction && selectedFunction.parameters
        ? selectedFunction.parameters.map((parameter) => {
          this.$set(this.parameterMenus, parameter.name, false);

          return {
            name: parameter.name,
            order: parameter.order,
            type: '',
            value: '',
          };
        })
        : [];

      this.$set(field, 'parameters', parameters);
      this.functionParamsExpanded = true;
    },

    toggleFunctionParams() {
      this.functionParamsExpanded = !this.functionParamsExpanded;
    },

    normalizeValueIn(field) {
      const values = Array.isArray(field.valuein) ? field.valuein : [];
      const normalizedValues = values.map((item) => {
        if (typeof item === 'string') {
          return { value: item };
        }

        return item;
      });

      this.$set(field, 'valuein', normalizedValues);
    },

    setDefaultValue(field) {
      this.$set(field, 'value', this.defaultValue);
    },

    syncDefaultValueFields() {
      if (this.field.type === 'VALOR_PADRAO') {
        this.setDefaultValue(this.field);
      }

      if (this.field.parameters && this.field.parameters.length) {
        this.field.parameters
          .filter((parameter) => parameter.type === 'VALOR_PADRAO')
          .forEach((parameter) => this.setDefaultValue(parameter));
      }
    },

    hydrateValueIn() {
      if (!this.hasMultipleValues || !this.field.value || this.field.valuein?.length) {
        return;
      }

      this.$set(
        this.field,
        'valuein',
        this.field.value.split('|').map((value) => ({ value })),
      );
    },

    sortParameters() {
      if (this.field.parameters && this.field.parameters.length) {
        this.field.parameters.sort((a, b) => a.order - b.order);
      }
    },

    formatDate(value) {
      if (!value) return '';

      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value.split('-').reverse().join('/');
      }

      return value;
    },
  },
};
</script>

<style scoped>
.condition-field {
  align-items: start;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(120px, 0.85fr) minmax(170px, 1.15fr);
  min-width: 260px;
}

.condition-field--compact {
  grid-template-columns: minmax(96px, 0.78fr) minmax(128px, 1fr);
  min-width: 220px;
}

.condition-field__title {
  color: var(--v-muted-foreground-base);
  font-size: 11px;
  font-weight: 700;
  grid-column: 1 / -1;
  line-height: 12px;
  text-transform: uppercase;
}

.condition-field__type,
.condition-field__value {
  align-self: start;
  min-width: 0;
}

.condition-field--compact ::v-deep .v-input__slot {
  min-height: 40px;
}

.condition-field__params {
  background: rgba(0, 0, 0, 0.035);
  border-radius: 6px;
  grid-column: 1 / -1;
  padding: 8px;
}

.condition-field__params-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  min-height: 22px;
}

.condition-field__params-title {
  color: var(--v-muted-foreground-base);
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  text-transform: uppercase;
}

.condition-field__param {
  align-items: start;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(90px, 0.6fr) minmax(120px, 0.8fr) minmax(160px, 1fr);
  margin-bottom: 8px;
}

.condition-field__param:last-child {
  margin-bottom: 0;
}

.condition-field__param-name {
  color: var(--v-foreground-base);
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  min-width: 0;
  padding-top: 5px;
}

.condition-field__param-name-text {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.condition-field--compact .condition-field__param {
  grid-template-columns: minmax(78px, 0.52fr) minmax(116px, 0.78fr) minmax(126px, 1fr);
}

.condition-field--compact .condition-field__param-name {
  font-size: 11px;
  line-height: 14px;
  padding-top: 4px;
}

.condition-field--compact ::v-deep .v-select__selection,
.condition-field--compact ::v-deep input {
  font-size: 13px;
}

@media (max-width: 960px) {
  .condition-field,
  .condition-field--compact,
  .condition-field__param {
    grid-template-columns: 1fr;
  }
}
</style>
